import { isDevEnv } from '@config/environment'
import { RequestType } from 'api-validations/stitch'
import type { ComponentProps } from 'react'
import image from 'shared-ui/assets/upside-down-tractor-no-padding.svg'
import { type FallbackComponent, ImageWithMessage } from 'shared-ui/components'
import { useLocalization } from 'shared-ui/localization'
import { ApiError } from 'utils'

interface Props extends ComponentProps<FallbackComponent> {
  imageSrc?: string
  heading?: string
  message?: string
  showDetails?: boolean
}

const fallback = {
  imageSrc: image,
  heading: 'Oops!',
  message: "Something went wrong, and we couldn't load the page properly.",
  showDetails: isDevEnv()
}

export const WebPageCrashFallback: React.FC<Props> = ({
  error,
  errorInfo,
  imageSrc = fallback.imageSrc,
  heading = fallback.heading,
  message = fallback.message,
  showDetails = fallback.showDetails
}) => {
  const { t } = useLocalization()
  const stackTrace = errorInfo?.componentStack?.split('\n').slice(0, 6)

  if (error instanceof ApiError) {
    const dataType = error.url.includes(RequestType.Default)
      ? 'static data'
      : error.url.includes(RequestType.Live)
        ? 'live data'
        : 'account information'
    message = `There was a problem loading your ${dataType}. The server responded with the status code ${error.status}.`
  }

  return (
    <ImageWithMessage imageSrc={imageSrc} message={message} heading={heading}>
      {showDetails && (
        <details className="relative mt-5 text-sm text-gray-500">
          <summary>{t(($) => $.labels.details)}</summary>
          <div className="absolute top-8 text-base text-gray-700">
            {error.message}
            {isDevEnv() && (
              <div className="mt-5 flex flex-col gap-1 text-green-500">
                <div className="whitespace-pre-wrap text-xs font-bold">Stack Trace:</div>
                {stackTrace?.map((item) => (
                  <div className="whitespace-pre-wrap text-xs" key={item}>
                    {item}
                  </div>
                ))}
              </div>
            )}
          </div>
        </details>
      )}
    </ImageWithMessage>
  )
}
